import { useEffect, useRef } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
  Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { ColumnKey } from './columnDefs';
import { getItemData, isItemData } from './itemData';

/**
 * Registers a single row as both a drag source and drop target. Uses a
 * `useEffect` tied to a stable ref (not a ref callback) so the returned
 * `combine(...)` cleanup is guaranteed to run before every re-registration —
 * a plain ref-callback would silently drop that cleanup on React 18, since
 * returning a cleanup function from a ref callback is only honored from
 * React 19 onward, leading to duplicate registrations on every re-render.
 */
export function useColumnOrderRow(
  columnKey: ColumnKey,
  disabled: boolean,
  onEdgeChange: (columnKey: ColumnKey, edge: Edge | null) => void,
) {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || disabled) {
      return undefined;
    }

    return combine(
      draggable({
        element,
        getInitialData: () => getItemData(columnKey),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isItemData(source.data),
        getData: ({ input }) =>
          attachClosestEdge(getItemData(columnKey), {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          }),
        onDrag: ({ self, source }) => {
          if (source.element === element) {
            onEdgeChange(columnKey, null);
            return;
          }
          onEdgeChange(columnKey, extractClosestEdge(self.data));
        },
        onDragLeave: () => onEdgeChange(columnKey, null),
        onDrop: () => onEdgeChange(columnKey, null),
      }),
    );
  }, [columnKey, disabled, onEdgeChange]);

  return ref;
}
